/**
 * search-ui.js
 * Requires custom-search.js (CustomSearch class) loaded first.
 *
 * Set window.CUSTOM_SEARCH_TOKEN in theme.liquid (site-wide, before this
 * script loads) so instant search works on every page, not just /search.
 *
 * <script>window.CUSTOM_SEARCH_TOKEN = "{{ settings.storefront_access_token }}";</script>
 * <script src="{{ 'custom-search.js' | asset_url }}" defer></script>
 * <script src="{{ 'search-ui.js' | asset_url }}" defer></script>
 */

(function () {

  // Inject dropdown styles once (constrained thumbnails, floats over content)
  function injectStyles() {
    if (document.getElementById("custom-search-dropdown-styles")) return;

    const style = document.createElement("style");
    style.id = "custom-search-dropdown-styles";
    style.textContent = `
      .custom-search-dropdown {
        position: absolute;
        top: 100%;
        left: 0;
        right: 0;
        background: #fff;
        border: 1px solid #e0e0e0;
        border-top: none;
        max-height: 400px;
        overflow-y: auto;
        z-index: 999;
        box-shadow: 0 6px 16px rgba(0,0,0,0.12);
      }
      .custom-search-dropdown__item {
        display: flex;
        align-items: center;
        gap: 10px;
        padding: 8px 12px;
        text-decoration: none;
        color: inherit;
        border-bottom: 1px solid #f0f0f0;
      }
      .custom-search-dropdown__item:last-child {
        border-bottom: none;
      }
      .custom-search-dropdown__item:hover {
        background: #f7f7f7;
      }
      .custom-search-dropdown__item img {
        width: 40px;
        height: 40px;
        min-width: 40px;
        object-fit: cover;
        border-radius: 4px;
        flex-shrink: 0;
      }
      .custom-search-dropdown__title {
        flex: 1;
        font-size: 13px;
        line-height: 1.3;
        overflow: hidden;
        text-overflow: ellipsis;
        text-wrap:wrap !important;
        white-space: nowrap;
      }
      .custom-search-dropdown__price {
        font-size: 13px;
        color: #666;
        white-space: nowrap;
        flex-shrink: 0;
      }
      .custom-search-dropdown__empty {
        padding: 12px;
        font-size: 13px;
        color: #999;
      }
    `;
    document.head.appendChild(style);
  }

  let sharedSearchPromise = null;

  function getSharedSearchIndex() {
    if (!sharedSearchPromise) {
      const search = new CustomSearch({
        shop: window.Shopify.shop,
        token: window.CUSTOM_SEARCH_TOKEN
      });
      sharedSearchPromise = search.buildIndex().then(() => search);
    }
    return sharedSearchPromise;
  }

  function escapeHtml(str) {
    const div = document.createElement("div");
    div.textContent = str == null ? "" : String(str);
    return div.innerHTML;
  }

  function formatMoney(amount, currency) {
    try {
      return new Intl.NumberFormat(undefined, {
        style: "currency",
        currency: currency
      }).format(amount);
    } catch (e) {
      return `${currency} ${amount}`;
    }
  }

  function productRowHtml(product, opts) {
    const contentColClass = opts.sidebar
      ? "eight columns omega align_left"
      : "twelve columns omega align_left";

    const priceHtml =
      product.available === false
        ? `<span class="sold-out-text">${escapeHtml(opts.soldOutText)}</span>`
        : `<span class="price"><span class="money">${formatMoney(
            product.price,
            product.currency
          )}</span></span>`;

    const img = product.image
      ? `<img src="${escapeHtml(product.image)}" alt="${escapeHtml(
          product.title
        )}" loading="lazy">`
      : "";

    return `
      <div class="product_row clearfix">
        <div class="four columns alpha">
          <a href="/products/${escapeHtml(product.handle)}" title="${escapeHtml(
      product.title
    )}">${img}</a>
        </div>
        <div class="${contentColClass}">
          <h5 class="sub_title">
            <a href="/products/${escapeHtml(product.handle)}" title="${escapeHtml(
      product.title
    )}">${escapeHtml(product.title)}</a>
          </h5>
          <div class="info">${priceHtml}</div>
        </div>
      </div>`;
  }

  // ---- Full search results page (/search?q=...) ----

  async function renderFullSearchResults() {
    const container = document.getElementById("js-search-results");
    if (!container) return;

    const params = new URLSearchParams(window.location.search);
    const query = params.get("q");
    if (!query) return;

    const sidebar = container.dataset.sidebar === "true";
    const soldOutText = container.dataset.soldOutText || "Sold out";

    container.classList.add("js-search-loading");

    const search = await getSharedSearchIndex();
    const results = search.search(query);

    container.classList.remove("js-search-loading");

    // If JS finds nothing, leave the container as-is so the
    // server-rendered Liquid results (if any) stay visible.
    if (!results.length) return;

    container.innerHTML = results.map((p) => productRowHtml(p, { sidebar, soldOutText })).join("");
  }

  // ---- Instant/live dropdown on any search input ----

  function renderDropdown(dropdown, results, query) {
    if (!results.length) {
      dropdown.innerHTML = `<div class="custom-search-dropdown__empty">No products found for "${escapeHtml(
        query
      )}"</div>`;
      dropdown.style.display = "block";
      return;
    }

    dropdown.innerHTML = results
      .slice(0, 6)
      .map(
        (p) => `
        <a href="/products/${escapeHtml(p.handle)}" class="custom-search-dropdown__item">
          ${p.image ? `<img src="${escapeHtml(p.image)}" alt="${escapeHtml(p.title)}">` : ""}
          <span class="custom-search-dropdown__title">${escapeHtml(p.title)}</span>
          <span class="custom-search-dropdown__price">${formatMoney(p.price, p.currency)}</span>
        </a>`
      )
      .join("");

    dropdown.style.display = "block";
  }

  function initInstantSearch() {
    const inputs = document.querySelectorAll('input[type="search"], input[name="q"]');

    inputs.forEach((input) => {
      if (input.dataset.customSearchBound) return;
      input.dataset.customSearchBound = "true";

      const dropdown = document.createElement("div");
      dropdown.className = "custom-search-dropdown";
      dropdown.style.display = "none";

      const parent = input.parentElement;
      const computedPosition = parent ? getComputedStyle(parent).position : "static";
      if (parent && computedPosition === "static") {
        parent.style.position = "relative";
      }
      if (parent) parent.appendChild(dropdown);

      let debounceTimer;

      input.addEventListener("input", () => {
        clearTimeout(debounceTimer);
        const value = input.value.trim();

        if (value.length < 2) {
          dropdown.style.display = "none";
          dropdown.innerHTML = "";
          return;
        }

        debounceTimer = setTimeout(async () => {
          const search = await getSharedSearchIndex();
          const results = search.search(value);
          renderDropdown(dropdown, results, value);
        }, 250);
      });

      document.addEventListener("click", (e) => {
        if (!dropdown.contains(e.target) && e.target !== input) {
          dropdown.style.display = "none";
        }
      });
    });
  }

  document.addEventListener("DOMContentLoaded", () => {
    injectStyles();
    initInstantSearch();
    renderFullSearchResults();
  });

})();